import { ReleasesKanban } from "@/components/releases/releases-kanban";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/projects/:slug/releases` — the release-planning kanban,
 * rendered inside the project layout. Cards are dragged between
 * release columns to re-plan; the left-most column collects
 * work items without a release.
 *
 * `?openOnly=0` widens the board to include closed releases as
 * frozen columns.
 */

const searchSchema = z.object({
  openOnly: z.literal("0").optional(),
});

type ReleaseColumn = {
  id: string;
  name: string;
  status: "open" | "locked" | "closed";
  dueDate: string | null;
};

type WorkItemCard = {
  id: string;
  gitlabIssueId: string;
  globalId: number;
  title: string;
  priority: string;
  releaseId: string | null;
  standalone: boolean;
  status: { name: string; color: string } | null;
  assignee: { name: string | null } | null;
  attachmentCount: number;
  isWaitingOnClient: boolean;
};

type ProjectIdentity = {
  id: string;
  slug: string;
  group: { path: string } | null;
};

type ReleasesResponse = {
  useReleases: boolean;
  releases: ReleaseColumn[];
  openCount: number;
  totalCount: number;
  workItems: WorkItemCard[];
  project: ProjectIdentity;
};

export const Route = createFileRoute("/_app/projects/$slug/releases/")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ openOnly: search.openOnly }),
  loader: ({ params, deps }) => {
    const qs = deps.openOnly === "0" ? "?openOnly=0" : "";
    return api.get<ReleasesResponse>(`/api/projects/${params.slug}/releases/kanban${qs}`);
  },
  component: ProjectReleasesPage,
});

function ProjectReleasesPage() {
  const { t } = useTranslation();
  const { useReleases, releases, openCount, totalCount, workItems } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const openOnly = search.openOnly !== "0";

  if (!useReleases) {
    return (
      <div className="space-y-6">
        <h2 className="font-heading text-xl font-semibold">{t("rel.title")}</h2>
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          <p className="font-medium text-foreground">{t("rel.disabledTitle")}</p>
          <p className="mt-2">
            <Trans
              i18nKey="rel.disabledHint"
              components={{
                link: (
                  <Link
                    to="/projects/$slug/settings"
                    params={{ slug }}
                    className="text-primary underline-offset-4 hover:underline"
                  />
                ),
              }}
            />
          </p>
        </div>
      </div>
    );
  }

  const closedCount = totalCount - openCount;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold">{t("rel.title")}</h2>
          <p className="text-muted-foreground text-sm mt-1">{t("rel.boardHint")}</p>
        </div>
        <Button asChild>
          <Link to="/projects/$slug/releases/new" params={{ slug }}>
            {t("rel.newRelease")}
          </Link>
        </Button>
      </div>

      <div className="flex items-center gap-3 text-sm">
        <span className="text-muted-foreground">{t("rel.showOnlyOpen")}</span>
        <Link
          to="/projects/$slug/releases"
          params={{ slug }}
          search={{}}
          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            openOnly
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-muted-foreground border-border hover:bg-accent"
          }`}
        >
          {t("rel.yes", { count: openCount })}
        </Link>
        <Link
          to="/projects/$slug/releases"
          params={{ slug }}
          search={{ openOnly: "0" }}
          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            !openOnly
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-muted-foreground border-border hover:bg-accent"
          }`}
        >
          {t("rel.no", { count: totalCount })}
        </Link>
        {closedCount > 0 && openOnly && (
          <span className="text-xs text-muted-foreground italic">
            {t("rel.closedHidden", { count: closedCount })}
          </span>
        )}
      </div>

      <ReleasesKanban slug={slug} releases={releases} workItems={workItems} />
    </div>
  );
}
